import React, { useContext, Fragment } from "react";
import {
  Box,
  Flex,
  Image,
  Text,
  UnorderedList,
  ListItem,
  Circle,
  Grid,
  GridItem,
  Spacer,
  OrderedList,
  Link,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from "@chakra-ui/react";
import { LinkContext } from "../../LinkContext";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";

const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const getPlacesWord = (numPlaces: number) => {
  const lastDigit = numPlaces % 10;

  if (lastDigit === 1) {
    return "место";
  } else if (
    lastDigit >= 2 &&
    lastDigit <= 4 &&
    (numPlaces > 14 || numPlaces < 12)
  ) {
    return "места";
  } else {
    return "мест";
  }
};

const getPointsWord = (numPoints: number) => {
  const lastDigit = numPoints % 10;

  if (lastDigit === 1) {
    return "балл";
  } else if (
    lastDigit >= 2 &&
    lastDigit <= 4 &&
    (numPoints > 14 || numPoints < 12)
  ) {
    return "балла";
  } else {
    return "баллов";
  }
};

interface ExamInfo {
  title: string;
  priority: number;
  id: number;
  minScore: number;
  exsType: string;
}

interface InfoblocksProps {
  specInfo: any;
  color: string;
}

const Infoblocks: React.FC<InfoblocksProps> = ({ specInfo, color }) => {
  const context = useContext(LinkContext);
  const info = [
    specInfo.guide_direction.guide_formative_unit
      ? specInfo.guide_direction.guide_formative_unit.name
      : null,
    `${specInfo.guide_direction.code} ${specInfo.guide_direction.program}`,
    capitalizeFirstLetter(
      specInfo.guide_direction.code.split(".")[1] === "03"
        ? "Бакалавриат"
        : specInfo.guide_direction.code.split(".")[1] === "05"
        ? "Специалитет"
        : specInfo.guide_direction.guide_education_level.name
    ),
    capitalizeFirstLetter(specInfo.guide_direction.guide_education_form.name),
    specInfo.guide_direction.guide_education_location.name,
  ];

  const innerWidth = window.innerWidth;

  const groupedExams = specInfo.guide_exams.reduce((acc: any, exam: any) => {
    const priority = exam.pivot.priority;
    if (!acc[priority]) {
      acc[priority] = [];
    }
    acc[priority].push(exam);
    return acc;
  }, {});

  const examsByPriority = Object.values(groupedExams);

  const latestScore = specInfo.scores.reduce((max: any, obj: any) => {
    return obj.year > max.year ? obj : max;
  }, specInfo.scores[0]);

  const actualPlaces = specInfo.all_places.reduce((max: any, obj: any) => {
    return obj.year > max.year ? obj : max;
  }, specInfo.all_places[0]);

  console.log("ap", actualPlaces);
  console.log("ls", latestScore);

  return (
    <Box>
      <Text as="b" fontSize="18px">
        Информация о программе
      </Text>
      <Grid
        templateColumns={{
          base: "1fr",
          xl: "300px 1fr",
        }}
        gap="20px"
        mt="20px"
      >
        <GridItem>
          <Box>
            <Grid
              templateColumns={["1fr", "repeat(2, 1fr)", "1fr"]}
              fontSize="16px"
            >
              <GridItem>
                <Box>
                  <UnorderedList>
                    {info
                      .filter((text: any) => text !== null)
                      .map((text: any) => (
                        <ListItem display="flex">
                          <Text>{text}</Text>
                        </ListItem>
                      ))}
                  </UnorderedList>
                </Box>
              </GridItem>
            </Grid>
          </Box>
        </GridItem>
        <GridItem>
          {!!actualPlaces && (specInfo.guide_direction.education_level_id === 1 ?  // box ниже только для бак/спец
            <Box p="20px" pb="32px" bg="#1D438A1A" w="100%" mb="20px">
              <Box>
                <Text
                  fontWeight="bold"
                  fontSize="20px"
                  textAlign="left"
                  ml="40px"
                >
                  Количество бюджетных мест в {actualPlaces.year} году
                </Text>
                <Grid templateColumns="repeat(4, 1fr)" mx="20px" my="10px">
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center" fontSize={16} fontWeight="bold">
                      Всего
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center" fontSize={16} fontWeight="bold">
                      Целевая квота
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center" fontSize={16} fontWeight="bold">
                      Особая квота
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center" fontSize={16} fontWeight="bold">
                      Отдельная квота
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center">
                      {actualPlaces.budget !== null ? (
                        <>
                          {actualPlaces.budget}{" "}
                          {getPlacesWord(actualPlaces.budget)}
                        </>
                      ) : (
                        "—"
                      )}
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center">
                      {actualPlaces.target !== null ? (
                        <>
                          {actualPlaces.target}{" "}
                          {getPlacesWord(actualPlaces.target)}
                        </>
                      ) : (
                        "—"
                      )}
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center">
                      {actualPlaces.exclusive !== null ? (
                        <>
                          {actualPlaces.exclusive}{" "}
                          {getPlacesWord(actualPlaces.exclusive)}
                        </>
                      ) : (
                        "—"
                      )}
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center">
                      {actualPlaces.special !== null ? (
                        <>
                          {actualPlaces.special}{" "}
                          {getPlacesWord(actualPlaces.special)}
                        </>
                      ) : (
                        "—"
                      )}
                    </Text>
                  </GridItem>
                </Grid>
              </Box>
              {actualPlaces.target_places.length > 0 && (
                <Accordion allowToggle mx="20px" mb="auto">
                  <AccordionItem>
                    <AccordionButton borderBottom={"1px solid #B5B4B4"}>
                      <AccordionIcon />
                      <Box
                        as="span"
                        flex="1"
                        textAlign="left"
                        fontWeight="bold"
                        fontSize={20}
                      >
                        Детализация целевого приёма
                      </Box>
                    </AccordionButton>
                    <AccordionPanel paddingLeft={0} paddingRight={0}>
                      <Box w="100%" fontSize="16px">
                        <Grid templateColumns="repeat(2, 1fr)">
                          <GridItem p={2} border={"1px solid #B5B4B4"}>
                            <Text textAlign="center" fontWeight="bold">
                              Название организации
                            </Text>
                          </GridItem>
                          <GridItem p={2} border={"1px solid #B5B4B4"}>
                            <Text textAlign="center" fontWeight="bold">
                              Количество мест
                            </Text>
                          </GridItem>
                          {actualPlaces.target_places.map((item: any) => (
                            <>
                              <GridItem p={2} border={"1px solid #B5B4B4"}>
                                <Text textAlign="center" fontSize={16}>
                                  {item.organization}
                                </Text>
                              </GridItem>
                              <GridItem p={2} border={"1px solid #B5B4B4"}>
                                <Text textAlign="center" fontSize={16}>
                                  {item.places}
                                </Text>
                              </GridItem>
                            </>
                          ))}
                        </Grid>
                      </Box>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              )}

              <Box mx="20px">
                <Text
                  fontWeight="bold"
                  fontSize="20px"
                  textAlign="left"
                  mt="20px"
                  ml="20px"
                >
                  Количество платных мест в {actualPlaces.year} году
                </Text>
                <Grid templateColumns="repeat(4, 1fr)" my="10px">
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center" fontSize={16} fontWeight="bold">
                      Места с оплатой стоимости обучения
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center" fontSize={16} fontWeight="bold">
                      Места с оплатой на базе СПО
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center" fontSize={16} fontWeight="bold">
                      Места с оплатой на базе ВО
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center" fontSize={16} fontWeight="bold">
                      Платный приём иностранцев
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center">
                      {actualPlaces.paid !== null ? (
                        <>
                          {actualPlaces.paid} {getPlacesWord(actualPlaces.paid)}
                        </>
                      ) : (
                        "—"
                      )}
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center">
                      {actualPlaces.paid_for_college !== null ? (
                        <>
                          {actualPlaces.paid_for_college}{" "}
                          {getPlacesWord(actualPlaces.paid_for_college)}
                        </>
                      ) : (
                        "—"
                      )}
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center">
                      {actualPlaces.paid_for_he !== null ? (
                        <>
                          {actualPlaces.paid_for_he}{" "}
                          {getPlacesWord(actualPlaces.paid_for_he)}
                        </>
                      ) : (
                        "—"
                      )}
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center">
                      {actualPlaces.paid_for_foreign !== null ? (
                        <>
                          {actualPlaces.paid_for_foreign}{" "}
                          {getPlacesWord(actualPlaces.paid_for_foreign)}
                        </>
                      ) : (
                        "—"
                      )}
                    </Text>
                  </GridItem>
                </Grid>
              </Box>
            </Box>
           : (specInfo.guide_direction.education_level_id === 3 || specInfo.guide_direction.education_level_id === 4) ?  
            <Box p="20px" pb="32px" bg="#1D438A1A" w="100%" mb="20px">
             <Box mx="20px">
                <Text
                  fontWeight="bold"
                  fontSize="20px"
                  textAlign="left"
                  mt="20px"
                  ml="20px"
                >
                  Количество мест в {actualPlaces.year} году
                </Text>
                <Grid templateColumns="repeat(3, 1fr)" my="10px">
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center" fontSize={16} fontWeight="bold">
                      Количество мест на бюджетной основе
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center" fontSize={16} fontWeight="bold">
                      Целевая квота
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center" fontSize={16} fontWeight="bold">
                      Места с оплатой стоимости обучения
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center">
                      {actualPlaces.budget !== null ? (
                        <>
                          {actualPlaces.budget}{" "}
                          {getPlacesWord(actualPlaces.budget)}
                        </>
                      ) : (
                        "—"
                      )}
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center">
                      {actualPlaces.target !== null ? (
                        <>
                          {actualPlaces.target}{" "}
                          {getPlacesWord(actualPlaces.target)}
                        </>
                      ) : (
                        "—"
                      )}
                    </Text>
                  </GridItem>
                  <GridItem p={2} border={"1px solid #B5B4B4"}>
                    <Text textAlign="center">
                      {actualPlaces.paid !== null ? (
                        <>
                          {actualPlaces.paid} {getPlacesWord(actualPlaces.paid)}
                        </>
                      ) : (
                        "—"
                      )}
                    </Text>
                  </GridItem>
                </Grid>
              </Box>
              {actualPlaces.target_places.length > 0 && (
                <Accordion allowToggle mx="20px" mb="auto">
                  <AccordionItem>
                    <AccordionButton borderBottom={"1px solid #B5B4B4"}>
                      <AccordionIcon />
                      <Box
                        as="span"
                        flex="1"
                        textAlign="left"
                        fontWeight="bold"
                        fontSize={20}
                      >
                        Детализация целевого приёма
                      </Box>
                    </AccordionButton>
                    <AccordionPanel paddingLeft={0} paddingRight={0}>
                      <Box w="100%" fontSize="16px">
                        <Grid templateColumns="repeat(2, 1fr)">
                          <GridItem p={2} border={"1px solid #B5B4B4"}>
                            <Text textAlign="center" fontWeight="bold">
                              Название организации
                            </Text>
                          </GridItem>
                          <GridItem p={2} border={"1px solid #B5B4B4"}>
                            <Text textAlign="center" fontWeight="bold">
                              Количество мест
                            </Text>
                          </GridItem>
                          {actualPlaces.target_places.map((item: any) => (
                            <>
                              <GridItem p={2} border={"1px solid #B5B4B4"}>
                                <Text textAlign="center" fontSize={16}>
                                  {item.organization}
                                </Text>
                              </GridItem>
                              <GridItem p={2} border={"1px solid #B5B4B4"}>
                                <Text textAlign="center" fontSize={16}>
                                  {item.places}
                                </Text>
                              </GridItem>
                            </>
                          ))}
                        </Grid>
                      </Box>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              )}
            </Box>
          : 
          <Box p="20px" pb="32px" bg="#1D438A1A" w="100%" mb="20px">
            <Box mx="20px">
             <Text
               fontWeight="bold"
               fontSize="20px"
               textAlign="left"
               mt="20px"
               ml="20px"
             >
               Количество мест в {actualPlaces.year} году
             </Text>
             <Grid templateColumns="repeat(2, 1fr)" my="10px">
               <GridItem p={2} border={"1px solid #B5B4B4"}>
                 <Text textAlign="center" fontSize={16} fontWeight="bold">
                   Количество мест на бюджетной основе
                 </Text>
               </GridItem>
               <GridItem p={2} border={"1px solid #B5B4B4"}>
                 <Text textAlign="center" fontSize={16} fontWeight="bold">
                   Места с оплатой стоимости обучения
                 </Text>
               </GridItem>
               <GridItem p={2} border={"1px solid #B5B4B4"}>
                 <Text textAlign="center">
                   {actualPlaces.budget !== null ? (
                     <>
                       {actualPlaces.budget}{" "}
                       {getPlacesWord(actualPlaces.budget)}
                     </>
                   ) : (
                     "—"
                   )}
                 </Text>
               </GridItem>
               <GridItem p={2} border={"1px solid #B5B4B4"}>
                 <Text textAlign="center">
                   {actualPlaces.paid !== null ? (
                     <>
                       {actualPlaces.paid} {getPlacesWord(actualPlaces.paid)}
                     </>
                   ) : (
                     "—"
                   )}
                 </Text>
               </GridItem>
             </Grid>
           </Box>
         </Box>
          )
        }
          <ResponsiveMasonry columnsCountBreakPoints={{ 500: 1, 750: 2 }}>
            <Masonry
              // columnsCount={innerWidth > 1600 ? 3 : innerWidth > 750 ? 2 : 1}
              gutter="20px"
            >
              {!!(specInfo.guide_exams.length > 0) && (
                <Fragment>
                  <Box p="20px" pb="32px" bg="#1D438A1A" w="100%">
                    <Text fontSize="20px">Вступительные испытания:</Text>
                    <Box mt="20px">
                      <OrderedList pl="15px">
                        {examsByPriority.map((examGroup: any) => (
                          <ListItem mb="10px">
                            {examGroup.map((ex: any) => ex.title).join(" или ")}
                          </ListItem>
                        ))}
                      </OrderedList>
                    </Box>
                  </Box>
                  <Box p="20px" pb="32px" bg="#1D438A1A" w="100%">
                    <Text fontSize="20px">
                      Минимальные баллы для подачи документов:
                    </Text>
                    <Box mt="20px">
                      {specInfo.guide_exams.map((exam: any) => (
                        <Flex mb="10px">
                          <Text>{exam.title}</Text>
                          <Spacer />
                          <Text textAlign="right">
                            {exam.pivot.min_score}{" "}
                            {getPointsWord(exam.pivot.min_score)}
                          </Text>
                        </Flex>
                      ))}
                    </Box>
                  </Box>
                </Fragment>
              )}
              {!!latestScore && (
                <Box p="20px" pb="32px" bg="#1D438A1A" w="100%">
                  <Text fontSize="20px">
                    Проходной балл на бюджетные места в {latestScore.year} году:
                  </Text>
                  <Box mt="20px">
                    <Text fontSize="42px" as="b">
                      {latestScore.threshold_budget / 1000}
                    </Text>
                  </Box>
                  {(specInfo.guide_direction.education_level_id == 5 ||
                    specInfo.guide_direction.education_level_id == 4) && (
                    <>
                      <Text fontSize="20px">
                        Проходной балл на платные места в {latestScore.year}{" "}
                        году:
                      </Text>
                      <Box mt="20px">
                        <Text fontSize="42px" as="b">
                          {latestScore.threshold_paid / 1000}
                        </Text>
                      </Box>
                    </>
                  )}
                  {!!latestScore.threshold_budget && (
                    <>
                      <Text fontSize="20px" mt="15px">
                        Средний балл на бюджетные места в {latestScore.year}{" "}
                        году:
                      </Text>
                      <Box mt="20px">
                        <Text fontSize="42px" as="b">
                          {latestScore.avg_budget / 1000}
                        </Text>
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {!!specInfo.last_prices && (
                <Box p="20px" pb="32px" bg="#1D438A1A" w="100%">
                  <Text fontSize="20px">
                    Стоимость обучения в {specInfo.last_prices[0].year} году (за
                    год):
                  </Text>
                  {specInfo.last_prices
                    .sort((a: any, b: any) => b.price - a.price)
                    .map((price: any) => (
                      <Box mt="20px">
                        <div className="mb-2">{price.price_type.title}:</div>
                        <Text fontSize="20px" as="b">
                          {(price.price / 1 + "")
                            .toString()
                            .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ")}
                          &nbsp;₽/год
                        </Text>
                      </Box>
                    ))}
                </Box>
              )}
            </Masonry>
          </ResponsiveMasonry>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default Infoblocks;
